import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const ImageCarousel: React.FC = () => {
  const [images, setImages] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalImage, setModalImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const { data, error } = await supabase.from('gallery').select('*').order('created_at', { ascending: false });
        if (error) throw error;
        
        const urls = data.map((item: any) => {
          if (item.url.startsWith("http")) return item.url;
          return item.url.startsWith("/") ? item.url : "/" + item.url;
        });
        setImages(urls);
        setLoading(false);
      } catch (err) {
        console.error("Resimler yüklenemedi:", err);
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  useEffect(() => {
    if (images.length === 0 || modalImage) return;
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images, modalImage]);

  useEffect(() => {
    if (modalImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [modalImage]);

  const goPrev = () =>
    setIndex((prev) => (prev - 1 + images.length) % images.length);
  const goTo = (i: number) => setIndex(i);

  if (loading) {
    return (
      <div className="w-full py-16 bg-gray-800 text-white text-center">
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="w-full py-32 bg-gray-800 text-white text-center flex flex-col items-center">
        <div className="text-6xl mb-4">🖼️</div>
        <h2 className="text-2xl font-semibold mb-2">Henüz görsel eklenmemiş.</h2>
        <p className="text-gray-300 max-w-md">
          Yakında Ayvalık Marin galerisi görsellerle dolacak. Takipte kalın!
        </p>
      </div>
    );
  }

  const leftIndex = (index - 1 + images.length) % images.length;
  const centerIndex = index;
  const rightIndex = (index + 1) % images.length;

  return (
    <section className="w-full py-16 bg-gray-800 flex flex-col items-center overflow-hidden h-auto">
      <div className="text-center px-4">
        <h2 className="text-3xl md:text-6xl font-bold opacity-20 text-white mb-2 mt-10 font-righteous">
          AYVALIK MARİN
        </h2>
        <h3 className="text-2xl md:text-4xl font-semibold text-white mb-12 -mt-2 md:-mt-4 font-montserrat">
          TEKNELERİMİZ
        </h3>
      </div>

      <div className="relative flex items-center justify-center w-full max-w-6xl px-4">
        {/* Sol ok */}
        <button
          onClick={goPrev}
          className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white shadow-md p-2 rounded-full z-20 hover:bg-gray-200"
        >
          ◀
        </button>

        <div className="flex items-center justify-center gap-4 w-full overflow-hidden">
          <img
            src={images[leftIndex]}
            alt="Left"
            className="hidden sm:block w-40 md:w-[450px] h-28 md:h-[300px] object-cover rounded-xl opacity-70 scale-95 transition-all duration-500 -translate-x-4"
          />
          <img
            src={images[centerIndex]}
            alt="Center"
            onClick={() => setModalImage(images[centerIndex])}
            className="cursor-pointer w-96 sm:w-[400px] md:w-[550px] h-52 sm:h-[280px] md:h-[360px] object-cover rounded-xl shadow-2xl scale-100 z-10 transition-all duration-500"
          />
          <img
            src={images[rightIndex]}
            alt="Right"
            className="hidden sm:block w-40 md:w-[450px] h-28 md:h-[300px] object-cover rounded-xl opacity-70 scale-95 transition-all duration-500 translate-x-4"
          />
        </div>

        {/* Sağ ok */}
        <button
          onClick={() => setIndex((prev) => (prev + 1) % images.length)}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white shadow-md p-2 rounded-full z-20 hover:bg-gray-200"
        >
          ▶
        </button>
      </div>

      {/* Thumbnail'lar */}
      <div className="mt-8 flex gap-2 md:gap-4 flex-wrap justify-center px-4">
        {images.map((img, i) => (
          <img
            key={i}
            src={img}
            alt={`Thumbnail ${i}`}
            onClick={() => goTo(i)}
            className={`w-14 md:w-20 h-10 md:h-14 object-cover rounded cursor-pointer border-2 transition duration-300 ${
              i === centerIndex
                ? "border-blue-500"
                : "border-transparent opacity-60 hover:opacity-100"
            }`}
          />
        ))}
      </div>

      {/* Modal */}
      {modalImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
          onClick={() => setModalImage(null)}
        >
          {/* Blur arka plan */}
          <div
            className="absolute top-0 left-0 w-full h-full bg-center bg-cover blur-xl scale-[1.2] brightness-50 z-0"
            style={{
              backgroundImage: `url(${modalImage})`,
            }}
          ></div>

          {/* Ana içerik */}
          <div
            className="relative w-[100dvw] h-[100dvh] z-10 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Orta Görsel */}
            <img
              src={modalImage}
              alt="selected"
              className="w-[100dvw] h-[100dvh] object-contain rounded-xl"
            />

            {/* Kapat butonu */}
            <button
              className="absolute top-2 right-2 text-white text-xl md:text-3xl font-bold bg-black/70 px-4 py-1 rounded-xl"
              onClick={() => setModalImage(null)}
            >
              &times;
            </button>

            {/* Sol buton */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIndex((prev) => (prev - 1 + images.length) % images.length);
                setModalImage(images[(index - 1 + images.length) % images.length]);
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full px-3 py-2"
            >
              ◀
            </button>

            {/* Sağ buton */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIndex((prev) => (prev + 1) % images.length);
                setModalImage(images[(index + 1) % images.length]);
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full px-3 py-2"
            >
              ▶
            </button>

            {/* Baloncuklar */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
              {images.map((_, i) => (
                <span
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    goTo(i);
                    setModalImage(images[i]);
                  }}
                  className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-300 ${
                    i === index
                      ? "bg-white scale-125 shadow-md"
                      : "bg-gray-400/70 hover:bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ImageCarousel;
